import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Clock, CheckCircle, XCircle, RefreshCw } from 'lucide-react';

const METHOD_COLORS = { GET: '#30d986', POST: '#4f8ef7', PUT: '#f5a623', PATCH: '#9d6ef5', DELETE: '#f05c5c' };

export default function TestRunsPanel({ projectId }) {
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, [projectId]);

  async function load() {
    setLoading(true);
    try {
      const res = await api.get(`/projects/${projectId}/test-runs?limit=100`);
      setRuns(res.data);
    } catch {}
    finally { setLoading(false); }
  }

  function fmtTime(ts) {
    return new Date(ts).toLocaleString();
  }

  return (
    <div style={sty.root}>
      <div style={sty.header}>
        <h2 style={sty.title}>Test Runs</h2>
        <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={13} /> Refresh</button>
      </div>

      <div style={sty.body}>
        <div style={sty.list}>
          {loading && <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 12 }}>Loading...</div>}
          {!loading && !runs.length && (
            <div style={sty.empty}>
              <Clock size={32} style={{ opacity: 0.2 }} />
              <p>No test runs yet.<br />Send a request to see history.</p>
            </div>
          )}
          {runs.map(run => (
            <div key={run.id} style={{ ...sty.runItem, ...(selected?.id === run.id ? sty.runItemActive : {}) }}
              onClick={() => setSelected(run)}>
              <div style={sty.runTop}>
                <span style={{ ...sty.method, color: METHOD_COLORS[run.method] || '#8892a4' }}>{run.method}</span>
                <span style={sty.epName}>{run.endpoint_name || 'Unknown'}</span>
                {run.success
                  ? <CheckCircle size={13} style={{ color: 'var(--accent-green)', flexShrink: 0 }} />
                  : <XCircle size={13} style={{ color: 'var(--accent-red)', flexShrink: 0 }} />}
              </div>
              <div style={sty.runMeta}>
                <span style={{ ...sty.statusCode, color: run.response_status < 300 ? 'var(--accent-green)' : run.response_status < 500 ? 'var(--accent-orange)' : 'var(--accent-red)' }}>
                  {run.response_status}
                </span>
                <span style={sty.runTime}>{run.response_time}ms</span>
                <span style={sty.runDate}>{fmtTime(run.ran_at)}</span>
              </div>
            </div>
          ))}
        </div>

        {selected ? (
          <div style={sty.detail}>
            <div style={sty.detailSection}>
              <div style={sty.detailTitle}>Request</div>
              <div style={sty.detailRow}><span style={sty.detailKey}>URL</span><span style={{ ...sty.detailVal, fontFamily: 'var(--mono)', wordBreak: 'break-all' }}>{selected.url}</span></div>
              <div style={sty.detailRow}><span style={sty.detailKey}>Method</span><span style={{ ...sty.detailVal, color: METHOD_COLORS[selected.method] }}>{selected.method}</span></div>
              {selected.request_body && (
                <div>
                  <div style={sty.detailKey}>Body</div>
                  <pre style={sty.codeBlock} className="mono">{
                    (() => { try { return JSON.stringify(JSON.parse(selected.request_body), null, 2); } catch { return selected.request_body; } })()
                  }</pre>
                </div>
              )}
            </div>
            <div style={sty.detailSection}>
              <div style={sty.detailTitle}>Response</div>
              <div style={sty.detailRow}>
                <span style={sty.detailKey}>Status</span>
                <span style={{ ...sty.detailVal, fontWeight: 700, color: selected.response_status < 300 ? 'var(--accent-green)' : 'var(--accent-red)' }}>{selected.response_status}</span>
              </div>
              <div style={sty.detailRow}><span style={sty.detailKey}>Time</span><span style={sty.detailVal}>{selected.response_time}ms</span></div>
              {selected.response_body && (
                <div>
                  <div style={sty.detailKey}>Body</div>
                  <pre style={sty.codeBlock} className="mono">{
                    (() => { try { return JSON.stringify(JSON.parse(selected.response_body), null, 2); } catch { return selected.response_body; } })()
                  }</pre>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={sty.noSel}>
            <Clock size={40} style={{ opacity: 0.15 }} />
            <p style={{ color: 'var(--text-muted)', marginTop: 12 }}>Select a run to see details</p>
          </div>
        )}
      </div>
    </div>
  );
}

const sty = {
  root: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' },
  title: { fontSize: 18, fontWeight: 700 },
  body: { display: 'flex', flex: 1, overflow: 'hidden' },
  list: { width: 280, borderRight: '1px solid var(--border)', overflow: 'auto', padding: '8px 6px' },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 16px', color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', gap: 10 },
  runItem: { padding: '10px 12px', borderRadius: 6, cursor: 'pointer', marginBottom: 2 },
  runItemActive: { background: 'var(--accent-dim)' },
  runTop: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 },
  method: { fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 10, minWidth: 40 },
  epName: { flex: 1, fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  runMeta: { display: 'flex', alignItems: 'center', gap: 10, paddingLeft: 46 },
  statusCode: { fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700 },
  runTime: { fontSize: 11, color: 'var(--text-muted)' },
  runDate: { fontSize: 10, color: 'var(--text-muted)', flex: 1, textAlign: 'right' },
  detail: { flex: 1, overflow: 'auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 20 },
  detailSection: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: 16 },
  detailTitle: { fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 },
  detailRow: { display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 8 },
  detailKey: { fontSize: 11, color: 'var(--text-muted)', width: 60, flexShrink: 0, paddingTop: 1 },
  detailVal: { fontSize: 12, color: 'var(--text-primary)' },
  codeBlock: { background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 6, padding: 12, fontSize: 11, color: '#a8d8a8', overflow: 'auto', maxHeight: 300, whiteSpace: 'pre-wrap', marginTop: 6 },
  noSel: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
};
